import React, { useEffect, useState } from "react";
import api, { brl, dateBR } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DollarSign, TrendingUp, ShoppingBag, AlertTriangle, Calendar, Receipt
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";

const PAYMENT_LABELS = { cash: "Dinheiro", credit: "Crédito", debit: "Débito", pix: "PIX" };
const COLORS = ["#2563EB", "#059669", "#F59E0B", "#7C3AED"];
const LINE_DOT = { r: 4 };

function Kpi({ icon: Icon, label, value, hint, tid, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-900 text-white",
    blue: "bg-blue-600 text-white",
    emerald: "bg-emerald-600 text-white",
    amber: "bg-amber-500 text-white",
    rose: "bg-rose-600 text-white",
  };
  return (
    <div className="card-outline p-5" data-testid={tid}>
      <div className="flex items-start justify-between">
        <div className={"w-10 h-10 rounded-lg flex items-center justify-center " + tones[tone]}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      </div>
      <div className="mt-4 kpi-num text-2xl sm:text-3xl text-slate-900">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-slate-500">Carregando…</div>;

  const pieData = data.payment_methods.map((m) => ({ name: PAYMENT_LABELS[m.method] || m.method, value: m.total }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Visão Geral</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Dashboard</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
        <Kpi icon={DollarSign} label="Faturamento Hoje" value={brl(data.revenue_today)} tid="kpi-revenue-today" tone="emerald" />
        <Kpi icon={TrendingUp} label="Vendas no Mês" value={brl(data.revenue_month)} hint={`${data.sales_count_month} vendas`} tid="kpi-revenue-month" tone="blue" />
        <Kpi icon={Receipt} label="Ticket Médio" value={brl(data.avg_ticket)} tid="kpi-avg-ticket" tone="slate" />
        <Kpi icon={AlertTriangle} label="Estoque Baixo" value={data.low_stock_count} hint={`${data.payable_due_today} contas vencidas`} tid="kpi-low-stock" tone="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="card-outline lg:col-span-2" data-testid="revenue-chart-card">
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Faturamento (últimos 7 dias)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.revenue_series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={12} />
                  <YAxis fontSize={12} tickFormatter={(v) => `R$${v}`} />
                  <Tooltip formatter={(v) => brl(v)} />
                  <Line type="monotone" dataKey="total" stroke="#2563EB" strokeWidth={2.5} dot={LINE_DOT} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="card-outline" data-testid="payment-chart-card">
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2"><DollarSign className="w-4 h-4" /> Formas de Pagamento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {pieData.length === 0 ? (
                <div className="text-slate-400 flex items-center justify-center h-full text-sm">Sem dados</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                      {pieData.map((entry, i) => <Cell key={`cell-${entry.name}`} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => brl(v)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="card-outline" data-testid="recent-sales-card">
        <CardHeader>
          <CardTitle className="font-display flex items-center gap-2"><ShoppingBag className="w-4 h-4" /> Vendas Recentes de Hoje</CardTitle>
        </CardHeader>
        <CardContent>
          {data.recent_sales.length === 0 ? (
            <div className="text-slate-400 text-sm py-6 text-center">Nenhuma venda ainda hoje.</div>
          ) : (
            <div className="space-y-2">
              {data.recent_sales.map((s) => (
                <div key={s.id} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <div>
                      <div className="text-sm font-medium">{dateBR(s.created_at)}</div>
                      <div className="text-xs text-slate-500">{s.items.length} itens • {s.user_name}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="kpi-num text-base">{brl(s.total)}</div>
                    <Badge variant="outline" className="text-[10px]">{PAYMENT_LABELS[s.payment_method]}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
